import { useQuery } from "@tanstack/react-query";
import * as api from "../endpoints";

/** Auth probe — drives the 401-redirect in AdminLayout. */
export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: api.adminMe,
    retry: false,
    staleTime: 30_000,
  });
}

export function useStats() {
  return useQuery({
    queryKey: ["stats"],
    queryFn: api.adminStats,
    staleTime: 30_000,
  });
}

export function useCockpit() {
  return useQuery({
    queryKey: ["cockpit"],
    queryFn: api.adminCockpit,
    staleTime: 20_000,
  });
}

export function useMerchantRequests(params: api.RequestListParams) {
  return useQuery({
    queryKey: ["requests", params],
    queryFn: () => api.listMerchantRequests(params),
    staleTime: 15_000,
  });
}

export function useMerchantRequest(id: number | null) {
  return useQuery({
    queryKey: ["request", id],
    queryFn: () => api.getMerchantRequest(id!),
    enabled: id != null,
    staleTime: 15_000,
  });
}

export function useApplications(params: api.ApplicationListParams) {
  return useQuery({
    queryKey: ["applications", params],
    queryFn: () => api.listApplications(params),
    staleTime: 15_000,
  });
}

export function useApplication(id: number | null) {
  return useQuery({
    queryKey: ["application", id],
    queryFn: () => api.getApplication(id!),
    enabled: id != null,
    retry: false,
    staleTime: 15_000,
  });
}

export function useApplicationPromotionCheck(id: number | null) {
  return useQuery({
    queryKey: ["applicationPromotionCheck", id],
    queryFn: () => api.checkApplicationPromotion(id!),
    enabled: id != null,
    staleTime: 10_000,
  });
}

export function useCandidates(params: api.QueryParams & { status?: string; q?: string }) {
  return useQuery({
    queryKey: ["candidates", params],
    queryFn: () => api.listCandidates(params),
    staleTime: 15_000,
  });
}

export function useCandidate(id: number | null) {
  return useQuery({
    queryKey: ["candidate", id],
    queryFn: () => api.getCandidate(id!),
    enabled: id != null,
    retry: false,
    staleTime: 15_000,
  });
}

export function useRoleBriefForPosition(positionId: number | null) {
  return useQuery({
    queryKey: ["roleBriefForPosition", positionId],
    queryFn: () => api.findRoleBriefForPosition(positionId!),
    enabled: positionId != null,
    staleTime: 15_000,
  });
}

export function useAdminPositions(params: api.AdminPositionListParams) {
  return useQuery({
    queryKey: ["adminPositions", params],
    queryFn: () => api.listAdminPositions(params),
    staleTime: 15_000,
  });
}

export function useAdminPosition(id: number | null) {
  return useQuery({
    queryKey: ["adminPosition", id],
    queryFn: () => api.getAdminPosition(id!),
    enabled: id != null,
    staleTime: 15_000,
  });
}

export function useRoleBriefs(params: api.QueryParams = {}) {
  return useQuery({
    queryKey: ["roleBriefs", params],
    queryFn: () => api.listRoleBriefs(params),
    staleTime: 15_000,
  });
}

export function useRoleBrief(id: number | null) {
  return useQuery({
    queryKey: ["roleBrief", id],
    queryFn: () => api.getRoleBrief(id!),
    enabled: id != null,
    retry: false,
    staleTime: 15_000,
  });
}

export function useSearchRuns(roleBriefId: number | null) {
  return useQuery({
    queryKey: ["searchRuns", roleBriefId],
    queryFn: () => api.listSearchRuns(roleBriefId!),
    enabled: roleBriefId != null,
    staleTime: 15_000,
  });
}

export function useRoleCandidates(roleBriefId: number | null) {
  return useQuery({
    queryKey: ["roleCandidates", roleBriefId],
    queryFn: () => api.listRoleCandidates(roleBriefId!),
    enabled: roleBriefId != null,
    staleTime: 15_000,
  });
}

export function useSourcedProfiles(searchRunId: number | null) {
  return useQuery({
    queryKey: ["sourcedProfiles", searchRunId],
    queryFn: () => api.listSourcedProfiles(searchRunId!),
    enabled: searchRunId != null,
    staleTime: 15_000,
    refetchInterval: 10_000,
  });
}

export function useSourcingTasks(searchRunId: number | null) {
  return useQuery({
    queryKey: ["sourcingTasks", searchRunId],
    queryFn: () => api.listSourcingTasks(searchRunId!),
    enabled: searchRunId != null,
    staleTime: 10_000,
    refetchInterval: 5_000,
  });
}
